import { useHistory, useLocation } from 'react-router';
import { defineMessages, useIntl } from 'react-intl';
import queryString from 'query-string';
import { useOwnHandle } from '@tokenized/sdk-react-private';
import { useSubscribedProfiles } from '#network/Billing';
import { useAnyProviderLoggedIn } from '#modules/api/User';
import { useMemo } from 'react';

const formattedPageNames = defineMessages({
  'log-in': {
    defaultMessage: 'Log in',
    description: 'Current page description in application window title',
  },
  'first-workspace': {
    defaultMessage: 'First workspace',
    description: 'Current page description in application window title',
  },
  overview: {
    defaultMessage: 'Overview',
    description: 'Current page description in application window title',
  },
  treasury: {
    defaultMessage: 'Treasury',
    description: 'Current page description in application window title',
  },
  instrument: {
    defaultMessage: 'Instrument',
    description: 'Current page description in application window title',
  },
  contracts: {
    defaultMessage: 'Contracts',
    description: 'Current page description in application window title',
  },
  contract: {
    defaultMessage: 'Contract editor',
    description: 'Current page description in application window title',
  },
  relationships: {
    defaultMessage: 'Relationships',
    description: 'Current page description in application window title',
  },
  settings: {
    defaultMessage: 'Settings',
    description: 'Current page description in application window title',
  },
  banking: {
    defaultMessage: 'Bank Accounts',
    description: 'Current page description in application window title',
  },
});

export function useCurrentPageInfo() {
  const intl = useIntl();

  const location = useLocation();
  const pathParts = (location?.pathname || '').split('/');
  const queryParts = queryString.parse(location?.search) || {};

  const account = useOwnHandle();
  const shortenedAccount = account && account.split('@')[0];

  const isLoggedIn = useAnyProviderLoggedIn();
  const profiles = useSubscribedProfiles();
  const needsFirstWorkspace = isLoggedIn && !profiles?.length;

  // log-in, sign-up, forgot
  // first-workspace
  // overview
  // treasury, instrument
  // contracts, contract
  // relationships
  // settings
  let page;
  if (queryParts.settings) {
    page = 'settings';
  } else if (needsFirstWorkspace) {
    page = 'first-workspace';
  } else {
    page = pathParts[1];
  }

  const formattedPage =
    formattedPageNames[page] && intl.formatMessage(formattedPageNames[page]);

  const result = useMemo(
    () => ({
      page,
      formattedPage,
      isLoggedIn,
      needsFirstWorkspace,
      account,
      shortenedAccount,
    }),
    [
      page,
      formattedPage,
      isLoggedIn,
      needsFirstWorkspace,
      account,
      shortenedAccount,
    ],
  );
  return result;
}

export function useIsShowingFullscreenOverlay(inSettings) {
  const location = useLocation();

  const { settings, modal } = queryString.parse(location.search) || {};

  return (!!settings && !inSettings) || !!modal;
}

export function locationToOpenSettings(
  currentLocation,
  settingsPage = 'previous',
  settingsEntity,
) {
  const queryParts = queryString.parse(currentLocation?.search) || {};
  return {
    ...currentLocation,
    search: queryString.stringify({
      ...queryParts,
      modal: undefined,
      settings: settingsPage,
      ...(settingsEntity !== undefined
        ? {
            settingsEntity: settingsEntity || undefined,
          }
        : {}),
    }),
    state: undefined,
  };
}

export function locationToCloseSettings(currentLocation) {
  const queryParts = queryString.parse(currentLocation?.search) || {};
  return {
    ...currentLocation,
    search: queryString.stringify({
      ...queryParts,
      settings: undefined,
      settingsEntity: undefined,
    }),
    state: undefined,
  };
}

export function locationToShowDeliveryAddresses(currentLocation) {
  const queryParts = queryString.parse(currentLocation?.search) || {};
  return {
    pathname: '/gbuy/addresses',
    search: queryString.stringify({
      entity: queryParts.entity,
    }),
    state: {
      // Allows app header back button to go to the source page
      navigatedFromOther: true,
    },
  };
}

export function useOpenModal(id, parameters) {
  const history = useHistory();
  const location = useLocation();
  return (extraParameters) =>
    history.push(
      locationToOpenModal(location, id, undefined, {
        ...parameters,
        ...extraParameters,
      }),
    );
}

export function locationToOpenModal(
  currentLocation,
  modalName,
  state,
  params,
  discardQueryParts = false,
) {
  const queryParts =
    (!discardQueryParts && queryString.parse(currentLocation?.search)) || {};
  return {
    ...currentLocation,
    search: queryString.stringify({
      ...queryParts,
      modal: `${modalName}?${queryString.stringify(params)}`,
    }),
    state,
  };
}

export function locationToCloseModal(
  currentLocation,
  discardQueryParts = false,
) {
  const { modal, ...queryParts } =
    (!discardQueryParts && queryString.parse(currentLocation?.search)) || {};
  return {
    ...currentLocation,
    search: queryString.stringify(queryParts),
    state: undefined,
  };
}

export function locationTo(currentLocation, customLocation) {
  return customLocation;
}

export function locationToUpdateQueryString(currentLocation, updates) {
  const queryParts = queryString.parse(currentLocation?.search) || {};
  return {
    ...currentLocation,
    search: queryString.stringify({
      ...queryParts,
      ...updates,
    }),
  };
}

export function locationToShowMainPage(currentLocation, path) {
  const queryParts = queryString.parse(currentLocation?.search) || {};
  return {
    pathname: path,
    search: queryString.stringify({
      entity: queryParts.entity,
    }),
  };
}

export function locationToUpdateMainPage(currentLocation, path) {
  return {
    ...currentLocation,
    pathname: path,
  };
}

export function locationToShowLoginPage(
  currentLocation,
  path,
  preserveAppLocation,
) {
  if (preserveAppLocation) {
    return {
      pathname: path,
      state: currentLocation?.state,
    };
  }
  return {
    pathname: path,
  };
}

export function locationToShowContract(currentLocation, id) {
  const queryParts = queryString.parse(currentLocation?.search) || {};
  return {
    pathname: `/contract/${encodeURIComponent(id)}`,
    search: queryString.stringify({
      entity: queryParts.entity,
    }),
    state: {
      // Allows app header back button to go to the source page/dialog
      navigatedFromOther: true,
    },
  };
}

export function locationToUpdateContractId(currentLocation, id) {
  return {
    ...currentLocation,
    pathname: `/contract/${encodeURIComponent(id)}`,
  };
}

export function locationToShowInstrumentActivity(currentLocation, id) {
  const queryParts = queryString.parse(currentLocation?.search) || {};
  return {
    pathname: `/instrument/${encodeURIComponent(id)}`,
    search: queryString.stringify({
      entity: queryParts.entity,
    }),
    state: {
      // Allows app header back button to go to the source page/dialog
      navigatedFromOther: true,
    },
  };
}

export function locationToShowLockboxDashboard(currentLocation, id) {
  const queryParts = queryString.parse(currentLocation?.search) || {};
  return {
    pathname: `/dashboard/${encodeURIComponent(id)}`,
    search: queryString.stringify({
      entity: queryParts.entity,
    }),
    state: {
      // Allows app header back button to go to the source page/dialog
      navigatedFromOther: true,
    },
  };
}

export function locationToShowLockboxStatements(currentLocation, id) {
  const queryParts = queryString.parse(currentLocation?.search) || {};
  return {
    pathname: `/statements/${encodeURIComponent(id)}`,
    search: queryString.stringify({
      entity: queryParts.entity,
    }),
    state: {
      // Allows app header back button to go to the source page/dialog
      navigatedFromOther: true,
    },
  };
}
